import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import {
  Card,
  CardHeader,
  CardPreview,
  Button,
  Text,
  Title3,
  Body1,
  Badge,
  Divider,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Spinner,
} from '@fluentui/react-components';
import {
  Document24Regular,
  ArrowDownloadOff24Regular,
  Add24Regular,
  ArrowDownload24Regular,
  DismissRegular,
  InfoRegular,
} from '@fluentui/react-icons';

import { PlacementDoc } from '../../models/placements';
import { TenderContract, Tender, Tendering } from '../../models/tendering';
import { Filter } from '../../models/filters';
import { useAppContext } from '../../AppContext';
import CreateContract from './PlacementDialogs/CreateContract';
import files from '../filePaths';
import { User } from '../../models/users';
import TenderContactDrawer from '../drawers/TenderContactDrawer';
import { Tag } from '../../models/tag';
import { getAllTags } from '../../services/tagService';
import TenderSettingsDrawer from '../TenderSettings/TenderSettingsDrawer';

interface ContractedComponentProps {
  contract: TenderContract;
  placement: PlacementDoc;
  placementDocID: string;
  baseTenders: Tender[];
  tenderingDocument: Tendering;
}

export const ContractedComponent: React.FC<ContractedComponentProps> = ({
  contract,
  placement,
  baseTenders,
  tenderingDocument
}) => {
  const [tender, setTender] = useState<Tender | null>(null);
  const [tenderDocument, setTenderDocument] = useState<Tender | null>(null);
  const [file, setFile] = useState<any>(null);
  const {user} = useAppContext();
  const [fileLoading, setFileLoading] = useState(false);
  const [templateFields, setTemplateFields] = useState<string[]>([]);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [openLogsDialog, setOpenLogsDialog] = useState(false);
  const [openTenderDrawer, setOpenTenderDrawer] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);

  // Contract template files - this would typically come from a service
  const contractFiles = [
    {
      fileName: 'Placeringsavtal Arbetskooperativ 2021',
      url: '/assets/ak/Placeringsavtal Arbetskooperativ 2021.docx',
      tenderingID: 2
    },
    {
      fileName: 'Placeringsavtal HVB Vuxna 2020',
      url: '/assets/hvb/Placeringsavtal-HVB-Vuxna-2020.docx',
      tenderingID: 11
    },
    // Add more files as needed...
  ];

  useEffect(() => {
    const acceptedTender = placement.tenders.find((t: any) => t.accepted);
    if (acceptedTender) {
      setTender(acceptedTender);
    }
  }, [placement.tenders]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tagsData = await getAllTags();
        setTags(tagsData);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };
    fetchTags();
  }, []);
  
  const isUserAuthorOfPlacement = () => {
    if (!placement?.user) return false;
  
    if (Array.isArray(placement.user)) {
      return placement.user.some((u: User) => u.userID === user.user.userID);
    }
    
    return placement.user.userID === user.user.userID;
  };

  const getTenderingFiles = () => {
    return files.filter(f => f.tenderingID === tenderingDocument.tenderingID && f.isPlacementFile);
  };

  const openCreateContractModal = () => {
    setOpenContractDialog(true);
    console.log('Opening contract modal...');
  };

  const createLogsPDF = (): void => {
    console.log(placement.logs);
    var template: string = `
    <div style="margin-bottom: 20px;">
      <h3 style="margin: 0 0 8px 0; font-size: 15px;">Förfrågan skapades</h3>
      <p style="margin: 3px 0;">${placement.logs.created.split('T')[0]} ${placement.logs.created.split('T')[1].slice(0, -5)} av ${placement.user.firstName} ${placement.user.lastName}</p>
    </div>
    `

    if(placement.logs.requestSent){
      template += `
      <div style="margin-bottom: 20px;">
        <h3 style="margin: 0 0 8px 0; font-size: 15px;">Massförfrågan skapades</h3>
        <p style="margin: 3px 0;">${placement.logs.requestSent.split('T')[0]} ${placement.logs.requestSent.split('T')[1].slice(0, -5)} av ${placement.logs.requestSent.userName}</p>
      </div>
      `
    }

    template += `
    <div style="margin: 25px 0 15px 0;">
      <h3 style="margin: 0 0 10px 0; font-size: 15px; padding-bottom: 5px; border-bottom: 1px solid #ccc;">Verksamheter ändringar</h3>
    </div>
    `

    placement.logs.tenderChanges.forEach((c: any) => {
      template += `
      <div style="margin-bottom: 12px; padding: 10px; border: 1px solid #ddd;">
        <p style="margin: 3px 0;"><strong>Ändring:</strong> ${c.tenderChange}</p>
        <p style="margin: 3px 0;"><strong>Verksamhet:</strong> ${c.tender}</p>
        <p style="margin: 3px 0;"><strong>Användare:</strong> ${c.userName}</p>
        <p style="margin: 3px 0;"><strong>Datum:</strong> ${c.date.split('T')[0]} ${c.date.split('T')[1].slice(0, -5)}</p>
        ${c.comment ? `<p style="margin: 8px 0 0 0; font-style: italic;"><strong>Kommentar:</strong> ${c.comment}</p>` : ''}
      </div>
      `
    })
    
    if(placement.logs.tenderPicked) {
      template += `
      <div style="margin-top: 20px;">
        <h3 style="margin: 0 0 8px 0; font-size: 15px;">Verksamhet vald</h3>
        <p style="margin: 3px 0;">${placement.logs.tenderPicked.date.split('T')[0]} ${placement.logs.tenderPicked.date.split('T')[1].slice(0, -5)} av ${placement.logs.tenderPicked.user}</p>
      </div>
      `
    }

    var content: string = generateHeader() + '<div style="margin: 0px 30px; page-break-inside: avoid;">' + template + '</div>';

    const options = {
      margin: [8, 8, 8, 8] as [number, number, number, number],
      autoPaging: 'text',
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const}
    };
    
    html2pdf().set(options).from(content).toPdf().get('pdf').then((pdf: any) => {
      pdf.save("logg");
    });

  }

  const generateHeader = (): string => {
    const currentDate = new Date().toLocaleDateString('sv-SE');
    return `<div>
    <div style="display: flex; justify-content: space-between; padding: 0 30px; font-size: 11px; color: #666;">
      <p style="margin: 0;">${currentDate}</p>
      <p style="margin: 0;">${placement.reference}</p>
    </div>    
    <div style="text-align: center; padding: 15px 30px; background-color: #e8e8e8; margin-bottom: 25px;">
      <h2 style="margin: 0 0 5px 0; font-size: 20px;">Logg för förfrågning</h2>
      <h3 style="margin: 0; font-size: 14px; font-weight: normal;">inom ramavtalet: ${placement.tenderingName}</h3>
    </div>
    </div>`
  }

  if (!tender) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="large" />
        <Text className="ml-2">Laddar...</Text>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Criteria Section */}
      <div className="space-y-4">
        <Text size={500} weight="semibold">Kriterier:</Text>
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex items-center space-x-2">
            <Text weight="semibold">Avtalsområde:</Text>
            <Badge appearance="filled">
              {placement.tenderingPart.tenderingPart}
            </Badge>
          </div>
          
          {placement.activeFilters.baseFilters.map((filter: Filter, index: number) => (
            <div key={index} className="flex items-center space-x-2">
              <Text weight="semibold">{filter.text}:</Text>
              <Badge>
                {filter.type === 'bool'
                  ? (filter.value === '1' ? 'Ja' : 'Nej')
                  : filter.value
                }
              </Badge>
            </div>
          ))}
          
          {placement.activeFilters.filters.map((filter: Filter, index: number) => (
            <div key={index} className="flex items-center space-x-2">
              <Text weight="semibold">{filter.text}:</Text>
              <Badge>
                {filter.type === 'bool'
                  ? (filter.value === '1' ? 'Ja' : 'Nej')
                  : filter.value
                }
              </Badge>
            </div>
          ))}
        </div>
      </div>

      <Divider />

      {/* Supplier Information */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Text weight="semibold">Leverantör:</Text>
          <Text>{tender.tenderName}</Text>
        </div>
        <div className="flex items-center space-x-2">
          <Text weight="semibold">E-Post:</Text>
          <Text>{tender.contact?.email}</Text>
        </div>
        <div className="flex items-center space-x-2">
          <Text weight="semibold">Mobiltelefon:</Text>
          <Text>{tender.contact?.phone}</Text>
        </div>
        <Button
          appearance="secondary"
          icon={<InfoRegular />}
          onClick={() => {
            setOpenLogsDialog(false);
            setOpenTenderDrawer(true);
          }}
        >
          Verksamhet
        </Button>
      </div>

      <Divider />

      {/* Placement Details (only for placement author) */}
      {user.user.userID === placement.user?.userID && (
        <>
          <div className="space-y-3">
            {placement.descriptionText && (
              <div>
                <Text weight="semibold">Behov:</Text>
                <Body1>{placement.descriptionText}</Body1>
              </div>
            )}
            {placement.backgroundText && (
              <div>
                <Text weight="semibold">Bakgrund:</Text>
                <Body1>{placement.backgroundText}</Body1>
              </div>
            )}
            {placement.plannedDate && (
              <div className="flex items-center space-x-2">
                <Text weight="semibold">När planeras placeringen:</Text>
                <Text>{new Date(placement.plannedDate).toLocaleDateString('sv-SE')}</Text>
              </div>
            )}
            {placement.answerDate && (
              <div className="flex items-center space-x-2">
                <Text weight="semibold">Svar senast:</Text>
                <Text>{new Date(placement.answerDate).toLocaleDateString('sv-SE')}</Text>
              </div>
            )}
          </div>
          <Divider />
        </>
      )}

      {/* Documents Section */}
      {((user.user.userID === placement.user?.userID) || user.roles.some((role: any) => role.roleType === 'General')) && (
        <div className="space-y-4">
          <div className="flex flex-col">
          <Text size={500} weight="semibold">Dokument</Text>

          {!isUserAuthorOfPlacement() && (
            <Body1 className="text-gray-600">
              Enbart handläggare som skapat förfrågan har möjlighet att skapa avtal. 
              Om annan handläggare ska skriva avtal får separat förfrågan skapas.
            </Body1>
          )}
          </div>

          {isUserAuthorOfPlacement() && contract && (
            <div className="space-y-2">
              {getTenderingFiles().map((file, index) => (
                <Card key={index} className="p-3 cursor-pointer hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <Document24Regular />
                    <a 
                      href={file.url} 
                      className="hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.fileName}
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          )}

          <div className="flex space-x-3">
            {isUserAuthorOfPlacement() && contract.template !== "" && (
              <Button
                disabled={!baseTenders}
                appearance="primary"
                icon={<Add24Regular />}
                onClick={openCreateContractModal}
              >
                Skapa Avtal
              </Button>
            )}
            
            {placement.logs && (
              <Button
                appearance="secondary"
                icon={<ArrowDownload24Regular />}
                onClick={() => setOpenLogsDialog(true)}
              >
                Visa logg
              </Button>
            )}
          </div>
        </div>
      )}
      <CreateContract
        open={openContractDialog}
        onClose={() => setOpenContractDialog(false)}
        tenderingDocument={tenderingDocument}
      />

      {/* Tender Drawer */}
      <TenderSettingsDrawer
        open={openTenderDrawer}
        onClose={() => setOpenTenderDrawer(false)}
        partialTender={tender}
        tab='info'
      />

      {/* Logs Dialog */}
      <Dialog open={openLogsDialog} onOpenChange={(event, data) => setOpenLogsDialog(data.open)}>
        <DialogSurface className="max-w-[700px]">
          <DialogBody>
            <DialogTitle
              action={
              <Button 
                shape='circular'
                appearance='subtle'
                icon={<DismissRegular/>}
                onClick={() => setOpenLogsDialog(false)}
              />
            }
            >Logg för förfrågning</DialogTitle>
            <DialogContent>
              <div className="flex flex-col gap-6">
                {/* Created */}
                <div className='flex flex-col'>
                  <Text weight="semibold" size={400} className="block mb-2">Förfrågan skapades</Text>
                  <Text size={300}>
                    {placement.logs?.created.split('T')[0]} {placement.logs?.created.split('T')[1].slice(0, -5)} av {placement.user.firstName} {placement.user.lastName}
                  </Text>
                </div>

                {/* Request Sent */}
                {placement.logs?.requestSent && (
                  <div className='flex flex-col'>
                    <Text weight="semibold" size={400} className="block mb-2">Massförfrågan skapades</Text>
                    <Text size={300}>
                      {placement.logs.requestSent.split('T')[0]} {placement.logs.requestSent.split('T')[1].slice(0, -5)}
                    </Text>
                  </div>
                )}

                {/* Tender Changes */}
                <div>
                  <Text weight="semibold" size={400}>
                    Verksamheter ändringar
                  </Text>
                  <div className="flex flex-col gap-4 max-h-96 overflow-y-auto">
                    {placement.logs?.tenderChanges.map((c: any, index: number) => (
                      <div key={index} className="p-3 flex flex-col gap-2 border border-gray-200">
                        <Text size={300}>
                          <strong>Ändring:</strong> {c.tenderChange}
                        </Text>
                        <Text size={300}>
                          <strong>Verksamhet:</strong> {c.tender}
                        </Text>
                        <Text size={300}>
                          <strong>Användare:</strong> {c.userName}
                        </Text>
                        <Text size={300}>
                          <strong>Datum:</strong> {c.date.split('T')[0]} {c.date.split('T')[1].slice(0, -5)}
                        </Text>
                        {c.comment && (
                          <Text size={300} className="italic mt-1">
                            <strong>Kommentar:</strong> {c.comment}
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tender Picked */}
                {placement.logs?.tenderPicked && (
                  <div className='flex flex-col'>
                    <Text weight="semibold" size={400} className="block mb-2">Verksamhet vald</Text>
                    <Text size={300}>
                      {placement.logs.tenderPicked.date.split('T')[0]} {placement.logs.tenderPicked.date.split('T')[1].slice(0, -5)} av {placement.logs.tenderPicked.user}
                    </Text>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setOpenLogsDialog(false)}
              >
                Stäng
              </Button>
              <Button
                appearance="primary"
                icon={<ArrowDownload24Regular />}
                onClick={() => {
                  createLogsPDF();
                  setOpenLogsDialog(false);
                }}
              >
                Ladda ner PDF
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ContractedComponent;
